if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    constructor() {
      super();

      this.sectionId = this.dataset.sectionId;
      this.productId = this.dataset.productId;

      // Find the main product form and buy button
      this.mainProductForm = document.querySelector(`product-form[data-section="${this.sectionId}"]`)
        || document.querySelector('#product-form-' + this.sectionId)
        || document.querySelector('product-form');

      this.mainBuyButton = document.querySelector(`#ProductSubmitButton-${this.sectionId}`)
        || document.querySelector('.product-form__submit');

      // Get sticky form elements
      this.stickyForm = this.querySelector('product-form');
      this.stickyButton = this.querySelector('.sticky-add-to-cart__button');
      this.stickyVariantInput = this.querySelector('.product-variant-id');

      // State
      this.isVisible = false;
      this.observer = null;

      // Initialize
      this.init();
    }

    init() {
      this.alwaysVisible = this.dataset.alwaysVisible === 'true';

      // Always-visible mode: no scroll observer, the bar is shown on load and
      // never hidden. It still syncs variant/quantity from the main form.
      if (this.alwaysVisible) {
        this.setupVariantSync();
        this.setupQuantitySync();
        this.setupViewportHandler();
        requestAnimationFrame(() => this.show());
        setTimeout(() => this.classList.add('animate'), 100);
        return;
      }

      if (!this.mainBuyButton) {
        console.warn('Sticky Add to Cart: Could not find main buy button');
        return;
      }

      // Setup intersection observer to watch the main buy button
      this.setupObserver();

      // Sync variant changes from main form to sticky form
      this.setupVariantSync();

      // Sync quantity changes (if quantity selector exists)
      this.setupQuantitySync();

      // Handle viewport changes (for mobile browser UI showing/hiding)
      this.setupViewportHandler();

      // Add animation class after a brief delay for smooth initial load
      setTimeout(() => this.classList.add('animate'), 100);
    }

    setupViewportHandler() {
      // Handle dynamic viewport changes (mobile browser UI)
      if (window.visualViewport) {
        // Use Visual Viewport API for accurate mobile browser UI detection
        const handleViewportChange = () => {
          // Force a reflow to ensure the element repositions
          if (this.isVisible) {
            this.style.bottom = '0px';
            // Trigger reflow
            void this.offsetHeight;
          }
        };

        window.visualViewport.addEventListener('resize', handleViewportChange);
        window.visualViewport.addEventListener('scroll', handleViewportChange);
      } else {
        // Fallback for browsers without Visual Viewport API
        let lastHeight = window.innerHeight;
        const handleResize = () => {
          const currentHeight = window.innerHeight;
          if (Math.abs(currentHeight - lastHeight) > 50) {
            // Significant height change, likely browser UI showing/hiding
            if (this.isVisible) {
              this.style.bottom = '0px';
              void this.offsetHeight;
            }
            lastHeight = currentHeight;
          }
        };

        window.addEventListener('resize', handleResize, { passive: true });
      }
    }

    setupObserver() {
      // Create intersection observer with appropriate threshold
      const observerOptions = {
        root: null, // viewport
        rootMargin: '0px',
        threshold: 0 // Trigger when any part leaves viewport
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          // Show sticky bar when main button is NOT visible
          if (!entry.isIntersecting) {
            this.show();
          } else {
            this.hide();
          }
        });
      }, observerOptions);

      // Start observing the main buy button
      this.observer.observe(this.mainBuyButton);
    }

    setupVariantSync() {
      // Listen for variant changes on the main product form
      if (this.mainProductForm) {
        // Listen for the variant change event (standard Shopify pattern)
        document.addEventListener('variant:change', (event) => {
          if (event.detail && event.detail.variant) {
            this.updateVariant(event.detail.variant);
          }
        });

        // Also watch for direct changes to the variant input field
        const mainVariantInput = this.mainProductForm.querySelector('[name="id"]');
        if (mainVariantInput) {
          const syncVariantId = () => {
            const variantId = mainVariantInput.value;
            if (this.stickyVariantInput && variantId) {
              this.stickyVariantInput.value = variantId;
              this.stickyVariantInput.disabled = false;
            }
          };

          // Sync immediately
          syncVariantId();

          // Watch for changes
          mainVariantInput.addEventListener('change', syncVariantId);

          // Use MutationObserver for dynamic updates
          const observer = new MutationObserver(syncVariantId);
          observer.observe(mainVariantInput, {
            attributes: true,
            attributeFilter: ['value']
          });
        }
      }

      // Listen for variant changes published by product-info.js. Dawn's pubsub is a
      // callback registry (subscribe/publish), not DOM events.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data || data.sectionId !== this.sectionId) return;
          // variant is null when the selected option combination doesn't exist
          this.updateVariant(data.variant || { id: null, available: false }, data.html);
        });
      }
    }

    setupQuantitySync() {
      // Find quantity input in main form
      const mainQuantityInput = this.mainProductForm?.querySelector('[name="quantity"]');

      if (mainQuantityInput) {
        const syncQuantity = () => {
          const stickyQuantityInput = this.stickyForm?.querySelector('[name="quantity"]');
          if (stickyQuantityInput) {
            stickyQuantityInput.value = mainQuantityInput.value;
          }
        };

        mainQuantityInput.addEventListener('change', syncQuantity);
        mainQuantityInput.addEventListener('input', syncQuantity);
      }
    }

    updateVariant(variant, html) {
      // Update variant ID in sticky form
      if (this.stickyVariantInput && variant.id) {
        this.stickyVariantInput.value = variant.id;
        this.stickyVariantInput.disabled = false;
      }

      // Update button state based on availability
      if (this.stickyButton) {
        const buttonText = this.stickyButton.querySelector('span');

        if (variant.available) {
          this.stickyButton.disabled = false;
          if (buttonText) {
            buttonText.textContent = buttonText.dataset.addToCartText ||
              window.variantStrings?.addToCart ||
              'Add to cart';
          }
        } else {
          this.stickyButton.disabled = true;
          if (buttonText) {
            buttonText.textContent = buttonText.dataset.soldOutText ||
              window.variantStrings?.soldOut ||
              'Sold out';
          }
        }
      }

      // Update price if price element exists
      this.updatePrice(variant, html);
    }

    updatePrice(variant, html) {
      const priceElement = this.querySelector('.sticky-add-to-cart__price');
      if (!priceElement) return;

      // Prefer the sticky price from the re-rendered section HTML (already rendered
      // for the selected variant, without badges)
      const renderedPrice = html?.querySelector(`sticky-add-to-cart[data-section-id="${this.sectionId}"] .sticky-add-to-cart__price`)
        || html?.querySelector('.sticky-add-to-cart__price');
      if (renderedPrice) {
        priceElement.innerHTML = renderedPrice.innerHTML;
        return;
      }

      if (!variant.price) return;

      // Find the main price element to copy its updated content
      const mainPrice = document.querySelector('.price[data-product-price]')
        || document.querySelector('.product__price .price');

      if (mainPrice) {
        // Clone the price HTML to keep formatting consistent
        priceElement.innerHTML = mainPrice.innerHTML;
      }
    }

    show() {
      if (this.isVisible) return;

      this.isVisible = true;
      this.classList.add('is-visible');

      // Reserve scroll space so the fixed bar never covers page content
      // (e.g. the product subtotal). Recompute on resize while visible.
      this.reserveSpace();
      if (!this._onResize) {
        this._onResize = () => { if (this.isVisible) this.reserveSpace(); };
        window.addEventListener('resize', this._onResize);
      }

      // Emit custom event for potential analytics or other listeners
      this.dispatchEvent(new CustomEvent('sticky-cart:show', {
        bubbles: true,
        detail: { productId: this.productId }
      }));
    }

    hide() {
      if (!this.isVisible) return;

      this.isVisible = false;
      this.classList.remove('is-visible');
      this.releaseSpace();

      // Emit custom event
      this.dispatchEvent(new CustomEvent('sticky-cart:hide', {
        bubbles: true,
        detail: { productId: this.productId }
      }));
    }

    reserveSpace() {
      const height = this.offsetHeight; // includes padding + safe-area inset
      document.documentElement.style.setProperty('--sticky-atc-height', `${height}px`);
      document.body.classList.add('sticky-atc-active');
    }

    releaseSpace() {
      document.body.classList.remove('sticky-atc-active');
    }

    disconnectedCallback() {
      // Clean up observer when element is removed
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
        this._onResize = null;
      }
      this.releaseSpace();
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
